import { useState, useEffect, Suspense, lazy } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuth, SupabaseAuthProvider } from './contexts/SupabaseAuthContext'
import LoadingAnimation from './components/LoadingAnimation'
import LoadingSpinner from './components/LoadingSpinner'
import ErrorBoundary from './components/ErrorBoundary'
import FloatingSidebar from './components/FloatingSidebar'
import WhatsAppChat from './components/whatsapp/WhatsAppChat'
import { PlayerProvider, usePlayer } from './contexts/PlayerContext'
import AuthCallback from './components/auth/AuthCallback'
import ReferralHandler from './components/ReferralHandler'
import PopupCallback from './pages/auth/PopupCallback'
import { Toaster, toast } from 'sonner'
import OnboardingModal from './components/auth/OnboardingModal'
import { supabase, supabaseMetrics } from './config/supabase'
import { ThemeProvider } from './contexts/ThemeContext'
import { useTokenSync } from './hooks/useTokenSync'
import { MetaPixelTracker } from './components/analytics/MetaPixelTracker'

// Páginas principais
import HomeIntroPage from './pages/HomeIntroPage'
import HomeLandingPage from './pages/HomeLandingPage'
import AuthPage from './pages/AuthPage'
import SoftwarePage from './pages/SoftwarePage'
import TokensPage from './pages/TokensPage'
import RechargeOrders from './pages/RechargeOrders'
import PixCheckoutPage from './pages/PixCheckoutPage'
import AsaasCheckoutPage from './pages/AsaasCheckoutPage'
import PaymentResultPage from './pages/PaymentResultPage'
import MelhoradorPage from './pages/software/MelhoradorPage'
import AdminLayout from './layouts/AdminLayout'
const MelhoradorCloudPage = lazy(() => import('./pages/software/MelhoradorCloudPage'))
const MontadorFastPage = lazy(() => import('./pages/software/MontadorFastPage'))
const DtfFabartPage = lazy(() => import('./pages/software/DtfFabartPage'))
const MontadorPage = lazy(() => import('./pages/software/MontadorPage'))
const SpotWhitePage = lazy(() => import('./pages/software/SpotWhitePage'))
const DtfProCheckoutPage = lazy(() => import('./pages/software/DtfProCheckoutPage'))
const OverbuilderPage = lazy(() => import('./pages/OverbuilderPage'))
const CloudEnhancerPage = lazy(() => import('./pages/apps/CloudEnhancerPage'))
const DirectAiVslPage = lazy(() => import('./pages/DirectAiVslPage'))

// Lazy loading para páginas pesadas
const AdminLicensesPage = lazy(() => import('./pages/admin/AdminLicensesPage'))
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'))
const SalesPage = lazy(() => import('./pages/admin/SalesPage'))
const WhatsappTestPage = lazy(() => import('./pages/admin/WhatsappTestPage'))
const NotificationQueuePage = lazy(() => import('./pages/admin/NotificationQueuePage'))
const UserLicensesPage = lazy(() => import('./pages/UserLicensesPage'))
const RenewalPage = lazy(() => import('./pages/RenewalPage'))
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'))

// Componente para rotas protegidas
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, isAuthenticated } = useAuth()
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="medium" message="Verificando..." />
      </div>
    )
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/auth/login" replace />
}

// Wrapper para WhatsAppChat que verifica se o player está ativo
const WhatsAppChatWrapper = () => {
  const { isFullscreenPlayerActive } = usePlayer()
  if (isFullscreenPlayerActive) {
    return null
  }
  return <WhatsAppChat />
}

function AppContent() {
  const location = useLocation()
  useTokenSync()
  const navigate = useNavigate()
  const { user, loading, isAuthenticated } = useAuth()

  // Tratamento de hash tokens (Launcher -> Web)
  useEffect(() => {
    const handleHashLogin = async () => {
      // Ignorar se estivermos nas rotas de callback de autenticação para evitar conflito
      if (location.pathname.startsWith('/auth/')) return

      const hash = window.location.hash
      if (!hash || !hash.includes('access_token')) return

      // Tentar extrair access_token e refresh_token
      const params = new URLSearchParams(hash.substring(1)) // remove #
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')
      // const expiresIn = params.get('expires_in')
      // const tokenType = params.get('token_type')

      if (accessToken) {
        console.log('[Auth] Detectado token via hash. Configurando sessão...')

        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          })

          if (error) {
            console.error('[Auth] Erro ao restaurar sessão do hash:', error)
            toast.error('Erro ao autenticar via Launcher')
          } else {
            console.log('[Auth] Sessão restaurada com sucesso.')
            toast.success('Login sincronizado com Launcher')
            // Limpar hash da URL para não expor token visualmente
            window.history.replaceState(null, '', window.location.pathname + window.location.search)
          }
        } catch (e) {
          console.error('[Auth] Exceção no hash login:', e)
        }
      }
    }

    handleHashLogin()
  }, [])

  const [showInitialAnimation, setShowInitialAnimation] = useState(() => {
    return !sessionStorage.getItem('initialAnimationShown')
  })
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const [onboardingInitial, setOnboardingInitial] = useState<any>(null)

  // Scroll automático para o topo ao navegar entre páginas
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      const mainElement = document.querySelector('main')
      if (mainElement) {
        mainElement.scrollTop = 0
      }
    }
    scrollToTop()
    const timeoutId = setTimeout(scrollToTop, 100)
    return () => clearTimeout(timeoutId)
  }, [location.pathname])

  // Verificar onboarding quando usuário autenticado mudar
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!isAuthenticated || !user?.id) {
        setOnboardingOpen(false)
        setOnboardingInitial(null)
        return
      }
      const { data, error } = await supabase
        .from('users')
        .select('onboarding_completed, display_name, email, phone_number, company_name, job_role, social_handle, birth_date, onboarding_bonus_awarded')
        .eq('uid', user.id)
        .single()

      if (error) {
        console.warn('Falha ao verificar onboarding:', error.message)
        return
      }

      const needsOnboarding = !Boolean(data?.onboarding_completed)
      setOnboardingOpen(needsOnboarding)
      setOnboardingInitial({
        display_name: data?.display_name ?? '',
        phone_number: data?.phone_number ?? '',
        company_name: data?.company_name ?? '',
        job_role: data?.job_role ?? '',
        social_handle: data?.social_handle ?? '',
        birth_date: data?.birth_date ?? '',
        onboarding_bonus_awarded: data?.onboarding_bonus_awarded ?? false,
      })
    }

    checkOnboarding()
  }, [isAuthenticated, user?.id])



  // Mostrar loading inicial apenas no refresh ou primeira visita
  /* 
  if (showInitialAnimation) {
    return (
      <LoadingAnimation onFinish={() => {
        setShowInitialAnimation(false)
        try { sessionStorage.setItem('initialAnimationShown', 'true') } catch { }
      }} />
    )
  }
  */

  // Determinar visibilidade do Header/Footer
  const path = location.pathname.toLowerCase()
  const shouldShowHeader = !(
    path === '/' ||
    path.startsWith('/melhorador-web') ||
    path === '/softwares' ||
    path === '/my-licenses' ||
    path.startsWith('/admin') ||
    path === '/overbuilder' ||
    path.startsWith('/direct-ai')
  )
  const shouldShowWhatsApp = !(
    path.startsWith('/melhorador-web') ||
    path === '/overbuilder'
  )
  const shouldMainOverflow = path !== '/' && path !== '/overbuilder'

  return (
    <div className="min-h-screen flex flex-col">
      <MetaPixelTracker />
      {shouldShowHeader && <FloatingSidebar />}

      <main className={shouldMainOverflow ? "flex-1 overflow-y-auto" : "flex-1 home-no-gradient"}>
        <Suspense fallback={
          <div className="h-full bg-black flex items-center justify-center">
            <LoadingSpinner size="large" message="Carregando página..." />
          </div>
        }>
          <Routes>
            {/* Home */}
            <Route path="/" element={<HomeLandingPage />} />
            <Route path="/intro" element={<HomeIntroPage />} />
            <Route path="/ref/:code" element={<ReferralHandler />} />

            {/* Auth Routes - Ordem importa: Específicas primeiro */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/popup-callback" element={<PopupCallback />} />
            <Route path="/auth/*" element={<AuthPage />} />

            {/* Admin */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="sales" element={<SalesPage />} />
              <Route path="licenses" element={<AdminLicensesPage />} />
              <Route path="whatsapp-test" element={<WhatsappTestPage />} />
              <Route path="notification-queue" element={<NotificationQueuePage />} />
            </Route>

            {/* Softwares */}
            <Route path="/softwares" element={<SoftwarePage />} />
            <Route path="/direct-ai" element={<DirectAiVslPage />} />
            <Route path="/software/melhorador" element={<MelhoradorPage />} />
            <Route path="/software/melhorador-cloud" element={<MelhoradorCloudPage />} />
            <Route path="/software/montador-fast" element={<MontadorFastPage />} />
            <Route path="/software/dtf-fabart" element={<DtfFabartPage />} />
            <Route path="/software/montador" element={<MontadorPage />} />
            <Route path="/software/spot-white" element={<SpotWhitePage />} />
            <Route path="/software/spot-cloud" element={<SpotWhitePage />} />
            <Route path="/software/dtf-factory-pro" element={<DtfProCheckoutPage />} />
            
            {/* Apps Mobile-First */}
            <Route path="/apps/enhancer" element={
              <ProtectedRoute>
                <CloudEnhancerPage />
              </ProtectedRoute>
            } />

            {/* Ferramentas Standalone */}
            <Route path="/overbuilder" element={
              <ProtectedRoute>
                <OverbuilderPage />
              </ProtectedRoute>
            } />

            {/* Tokens e Pagamentos */}
            <Route path="/renew-license" element={<RenewalPage />} />
            <Route path="/tokens" element={<TokensPage />} />
            <Route path="/my-recharges" element={
              <ProtectedRoute>
                <RechargeOrders />
              </ProtectedRoute>
            } />
            <Route path="/checkout/pix" element={
              <ProtectedRoute>
                <PixCheckoutPage />
              </ProtectedRoute>
            } />
            <Route path="/checkout/asaas" element={
              <ProtectedRoute>
                <AsaasCheckoutPage />
              </ProtectedRoute>
            } />
            <Route path="/purchase/:purchaseId" element={
              <ProtectedRoute>
                <PixCheckoutPage />
              </ProtectedRoute>
            } />
            <Route path="/payment/:status" element={<PaymentResultPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />

            {/* Perfil */}
            <Route path="/my-licenses" element={
              <ProtectedRoute>
                <UserLicensesPage />
              </ProtectedRoute>
            } />



            {/* Fallback - Redirecionar para home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      {shouldShowWhatsApp && <WhatsAppChatWrapper />}

      {isAuthenticated && user?.id && (
        <OnboardingModal
          isOpen={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
          userUid={user.id}
          userEmail={(user as any)?.email}
          initialData={onboardingInitial ?? undefined}
        />
      )}
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <PlayerProvider>
        <SupabaseAuthProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </SupabaseAuthProvider>
        <Toaster
          position="top-right"
          richColors
          closeButton
          duration={5000}
          toastOptions={{
            style: { zIndex: 100000 },
          }}
        />
      </PlayerProvider>
    </ErrorBoundary>
  )
}

export default App
